import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAuth } from '@lib/auth';
import { isPostHogLoaded, onPostHogLoaded, posthog } from '@lib/posthog.lib';
import type { RootState } from '@lib/redux.lib/store';

import {
  isClarityTracking,
  setClarityTag,
  startClarityTracking,
  stopClarityTracking,
  stopClarityTrackingForOtherUser,
} from './clarityClient';
import { resolveClaritySessionIdentity } from './claritySessionUser';

const clarityProjectId = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;
const isProduction = process.env.NEXT_PUBLIC_NODE_ENV === 'production';
const isClarityEnabled = isProduction && Boolean(clarityProjectId);

const syncSessionTags = (
  userId: number,
  impersonatedUserId: number | null,
  email: string | null
): void => {
  setClarityTag('user_id', String(userId));
  setClarityTag(
    'impersonated_target_id',
    impersonatedUserId != null ? String(impersonatedUserId) : ''
  );

  setClarityTag('email', email ?? '');

  const posthogId = posthog.get_distinct_id?.();

  setClarityTag('posthog_id', posthogId ? String(posthogId) : '');
};

type TrackedIdentity = {
  userId: number;
  impersonatedUserId: number | null;
  email: string | null;
};

/**
 * Initializes Microsoft Clarity for every authenticated session user once the
 * project id is present, then tags the session with the admin user id, optional
 * impersonated target, session-user email, and PostHog distinct id.
 */
export const ClarityLifecycle = (): null => {
  const { authUserSession, loading } = useAuth();
  const profile = useSelector(
    (state: RootState) => state.app.user.profile ?? null
  );
  const impersonatedUserId = useSelector(
    (state: RootState) => state.app.user.impersonatedUser ?? null
  );
  const [isTracking, setIsTracking] = useState(false);
  const [trackedIdentity, setTrackedIdentity] =
    useState<TrackedIdentity | null>(null);
  const [isPostHogReady, setIsPostHogReady] = useState(isPostHogLoaded);
  const previousSessionRef =
    useRef<{
      userId: number;
      email: string | null;
    } | null>(null);

  useEffect(() => {
    if (isPostHogReady) {
      return;
    }

    return onPostHogLoaded(() => {
      setIsPostHogReady(true);
    });
  }, [isPostHogReady]);

  useEffect(() => {
    if (!isClarityEnabled) {
      return;
    }

    if (authUserSession == null) {
      stopClarityTracking();
      setIsTracking(false);
      setTrackedIdentity(null);
      previousSessionRef.current = null;

      return;
    }

    // Auth bootstrap and logout both set loading before session is cleared.
    // Stop recording immediately so teardown cannot leave an active session.
    if (loading === true) {
      stopClarityTracking();
      setIsTracking(false);

      return;
    }

    const identity = resolveClaritySessionIdentity(
      authUserSession,
      profile,
      impersonatedUserId
    );

    if (identity == null) {
      stopClarityTracking();
      setIsTracking(false);
      setTrackedIdentity(null);
      previousSessionRef.current = null;

      return;
    }

    const { userId, impersonatedUserId: targetId, email } = identity;
    const previousSession = previousSessionRef.current;

    // Id or email change means another user session — restart Clarity.
    if (
      previousSession != null &&
      (previousSession.userId !== userId || previousSession.email !== email)
    ) {
      stopClarityTracking();
    }

    stopClarityTrackingForOtherUser(userId);
    previousSessionRef.current = { userId, email };
    setTrackedIdentity({ userId, impersonatedUserId: targetId, email });
    setIsTracking(startClarityTracking(clarityProjectId, userId));
  }, [loading, authUserSession, profile, impersonatedUserId]);

  useEffect(() => {
    if (!isClarityEnabled || !isTracking || trackedIdentity == null) return;

    // Re-run when PostHog becomes ready or the session user changes so
    // posthog_id is not left anonymous / unset after a late identify.
    syncSessionTags(
      trackedIdentity.userId,
      trackedIdentity.impersonatedUserId,
      trackedIdentity.email
    );
  }, [
    isTracking,
    trackedIdentity,
    isPostHogReady,
    profile?.id,
    profile?.email,
  ]);

  useEffect(
    () => () => {
      if (!isClarityEnabled) return;

      stopClarityTracking();
    },
    []
  );

  return null;
};
